import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import he from "he";

import { getCurrentTime } from "./utils";
import { apiFetch } from "../../utils/api";
import TicketCard from "./TicketCard";

function parseJsonSafe(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return null;
  }
}

function isPreviewCourseSeparator(item) {
  const name = String(item?.Name || "").trim();
  const category = String(item?.Category || "").trim();
  return /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
}

function normalizeBumpedOrderForPreview(order) {
  const ticket = parseJsonSafe(order?.ticket_json) || {};
  const rawItems = Array.isArray(ticket?.Item)
    ? ticket.Item
    : ticket?.Item
      ? [ticket.Item]
      : [];

  const normalizedItems = rawItems
    .filter((item) => !isPreviewCourseSeparator(item))
    .map((item, index) => {
    const status = String(item?.status || "").toLowerCase();
      return {
        ...item,
        ID: item?.ID ?? item?.id ?? `preview-${order?.ticket_instance_id || "ticket"}-${index}`,
        Name: item?.Name ?? item?.name ?? "",
        Category: item?.Category ?? item?.category ?? "",
        Quantity: item?.Quantity ?? item?.quantity ?? 1,
        course: item?.course ?? 0,
        createdAt:
          item?.createdAt ||
          item?.created_at ||
          order?.received_at ||
          order?.receivedAt ||
          null,
        isBumped: item?.isBumped ?? status === "bumped",
        isVoided:
          item?.isVoided != null
            ? item.isVoided
            : Number(item?.TransType) === 2 || status === "voided",
        isParked: item?.isParked ?? status === "parked",
        Condiment: Array.isArray(item?.Condiment)
          ? item.Condiment
          : item?.Condiment
            ? [item.Condiment]
            : [],
        PreModifier:
          item?.PreModifier && typeof item.PreModifier === "object"
            ? item.PreModifier
            : {},
      };
    });

  return {
    ...order,
    createdAt: order?.createdAt || order?.received_at || order?.receivedAt || null,
    table_name: order?.table_name || ticket?.GuestTable || null,
    server_name: order?.server_name || ticket?.ServerName || null,
    user_info: order?.user_info || ticket?.UserInfo || null,
    items: normalizedItems,
  };
}

function isOrderFullyVoided(order) {
  const realItems = Array.isArray(order?.items)
    ? order.items.filter(
        (item) =>
          !item?.isCourseSeparator &&
          !item?.isMessage &&
          !item?.isFireItem &&
          !isPreviewCourseSeparator(item)
      )
    : [];

  return realItems.length > 0 && realItems.every((item) => item?.isVoided);
}

function FooterIcon({ icon }) {
  return <Icon icon={icon} className="h-6 w-6 shrink-0" aria-hidden="true" />;
}

export default function FooterBar({
  stationId,
  role,
  screenMountId,
  lastOrderId,
  orders = [],
  currentPage = 0,
  totalPages = 1,
  pageIndicatorText = null,
  isLineItemMode = false,
  onFireTicket,
  onFireCourse,
  onFireItem,
  onViewFullTicket,
  settings,
  onBumpAll,
  onParkClick,
  onExposeClick,
  showExposeButton = false,
  onSortClick,
  showSortButton = false,
  onPinClick,
  showPinButton = false,
  refreshFromServer,
  onTicketUnbumped,
  onPrintTicket,
  onPrintCourse,
  summaryToggleSignal = 0,
  unbumpMenuSignal = 0,
}) {
  const [clock, setClock] = useState(getCurrentTime());
  const [bumpedOrders, setBumpedOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [showFireMenu, setShowFireMenu] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showFreeformToolbox, setShowFreeformToolbox] = useState(false);

  const fireMenuRef = useRef(null);
  const printMenuRef = useRef(null);
  const freeformToolboxRef = useRef(null);
  const [selectedBumpedOrder, setSelectedBumpedOrder] = useState(null);

  const [confirmUnbump, setConfirmUnbump] = useState({
    open: false,
    ticket_instance_id: null,
    order_id: null,
  });
  const selectedBumpedOrderFullyVoided = isOrderFullyVoided(selectedBumpedOrder);
  const bumpBarUnbumpSelectedRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setClock(getCurrentTime()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showSummary) {
      generateSummary();
    }
  }, [orders, showSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        showFireMenu &&
        fireMenuRef.current &&
        !fireMenuRef.current.contains(event.target)
      ) {
        setShowFireMenu(false);
      }

      if (
        showPrintMenu &&
        printMenuRef.current &&
        !printMenuRef.current.contains(event.target)
      ) {
        setShowPrintMenu(false);
      }

      if (
        showFreeformToolbox &&
        freeformToolboxRef.current &&
        !freeformToolboxRef.current.contains(event.target)
      ) {
        setShowFreeformToolbox(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showFireMenu, showPrintMenu, showFreeformToolbox]);

  const fetchBumpedOrders = async () => {
    try {
      const res = await apiFetch(`/orders/bumped-today?station_id=${stationId}`);

      if (res.status === 204) {
        setBumpedOrders([]);
        return;
      }

      const data = await res.json();
      const asArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.orders)
          ? data.orders
          : Array.isArray(data?.rows)
            ? data.rows
            : [];
      const normalized = asArray.map(normalizeBumpedOrderForPreview);

      setBumpedOrders(normalized);
      setSelectedBumpedOrder((current) => {
        if (!normalized.length) return null;
        if (!current) return normalized[0];
        const match = normalized.find(
          (order) => order.ticket_instance_id === current.ticket_instance_id
        );
        return match || normalized[0];
      });
    } catch (err) {
      console.error("Failed to fetch bumped tickets:", err);
      setBumpedOrders([]);
      setSelectedBumpedOrder(null);
    }
  };

  const doUnbump = async ({ ticket_instance_id, order_id }, unbumpAllItems) => {
    const targetOrder = bumpedOrders.find(
      (order) => order.ticket_instance_id === ticket_instance_id
    );

    if (isOrderFullyVoided(targetOrder)) {
      return false;
    }

    const res = await apiFetch(`/orders/${ticket_instance_id}/unbump`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        unbumpAllItems
          ? {
              unbump_items: true,
              sender_station_id: stationId,
              sender_role: role,
              sender_screen_id: screenMountId,
            }
          : {
              sender_station_id: stationId,
              sender_role: role,
              sender_screen_id: screenMountId,
            }
      ),
    });

    if (!res.ok) {
      if (res.status === 409) {
        console.warn("Blocked unbump for fully voided ticket.");
      }
      console.error("Failed to unbump order:", await res.text());
      return false;
    }

    setBumpedOrders((prev) =>
      prev.filter((o) => o.ticket_instance_id !== ticket_instance_id)
    );

    if (typeof onTicketUnbumped === "function") {
      onTicketUnbumped({
        ticket_instance_id,
        order_id,
        unbump_items: unbumpAllItems === true,
      });
    }

    if (typeof refreshFromServer === "function") {
      await refreshFromServer();
    }

    return true;
  };

  const openUnbumpModal = async () => {
    await fetchBumpedOrders();
    setShowModal(true);
  };

  const generateSummary = (
    separate_itempreps_in_summary =
      settings?.separate_itempreps_in_summary ?? true,
    hide_preps_in_summary = settings?.hide_preps_in_summary ?? false
  ) => {
    const courseMap = new Map();

    for (const order of orders) {
      for (const item of order.items || []) {
        if (
          item.status === "voided" ||
          item.isVoided ||
          item.status === "bumped" ||
          item.status === "parked" ||
          item.isParked ||
          item.isBumped ||
          item.isCourseSeparator ||
          item.isMessage ||
          item.isFireItem
        ) {
          continue;
        }

        const course = item.course ?? 0;
        const name = he.decode(item.Name?.trim() || "Unnamed Item");
        const qty = parseInt(item.Quantity, 10) || 1;
        const condiments = Array.isArray(item.Condiment) ? item.Condiment : [];

        if (!courseMap.has(course)) {
          courseMap.set(course, new Map());
        }

        const itemMap = courseMap.get(course);

        if (!itemMap.has(name)) {
          itemMap.set(name, {
            qty: 0,
            condiments: {},
            preps: [],
          });
        }

        const entry = itemMap.get(name);
        entry.qty += qty;

        if (hide_preps_in_summary) {
          continue;
        }

        if (separate_itempreps_in_summary) {
          for (let i = 0; i < qty; i += 1) {
            const condList = condiments.map((c) =>
              he.decode(c.Name?.trim() || "Unnamed Condiment")
            );
            entry.preps.push(condList);
          }
        } else {
          for (const cond of condiments) {
            const condName = he.decode(cond.Name?.trim() || "Unnamed Condiment");
            entry.condiments[condName] = (entry.condiments[condName] || 0) + 1;
          }
        }
      }
    }

    const result = [...courseMap.entries()]
      .sort(([a], [b]) => a - b)
      .map(([courseIndex, itemMap]) => ({
        course: courseIndex,
        items: [...itemMap.entries()].map(([name, data]) => {
          if (hide_preps_in_summary) {
            return { name, qty: data.qty, condiments: [] };
          }

          if (separate_itempreps_in_summary) {
            return {
              name,
              qty: data.qty,
              prepGroups: data.preps.map((group) =>
                group.map((condName) => ({ condName, count: 1 }))
              ),
            };
          }

          return {
            name,
            qty: data.qty,
            condiments: Object.entries(data.condiments).map(
              ([condName, count]) => ({ condName, count })
            ),
          };
        }),
      }));

    setSummaryData(result);
    setShowSummary(true);
  };

  useEffect(() => {
    if (!summaryToggleSignal) return;
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    generateSummary();
  }, [summaryToggleSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!unbumpMenuSignal) return;
    openUnbumpModal();
  }, [unbumpMenuSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bumpBarUnbumpSelectedRef.current = async () => {
      if (!selectedBumpedOrder || selectedBumpedOrderFullyVoided) return;
      const ok = await doUnbump(
        {
          ticket_instance_id: selectedBumpedOrder.ticket_instance_id,
          order_id: selectedBumpedOrder.order_id ?? null,
        },
        isLineItemMode
      );
      if (ok) setShowModal(false);
    };
  });

  useEffect(() => {
    if (!showModal || settings?.enable_bump_bar !== true || confirmUnbump.open) {
      return undefined;
    }

    const moveSelection = (direction) => {
      setSelectedBumpedOrder((current) => {
        if (!bumpedOrders.length) return null;
        const currentIndex = bumpedOrders.findIndex(
          (order) => order.ticket_instance_id === current?.ticket_instance_id
        );
        const fallbackIndex = direction < 0 ? bumpedOrders.length - 1 : 0;
        const nextIndex =
          currentIndex === -1
            ? fallbackIndex
            : (currentIndex + direction + bumpedOrders.length) % bumpedOrders.length;
        return bumpedOrders[nextIndex];
      });
    };

    const handleBumpBarModalKeyDown = (event) => {
      const key = event.key;
      const isPrevious = key === "ArrowRight" || key === "ArrowUp";
      const isNext = key === " " || key === "ArrowDown";
      const isRedraw = key === "Enter";
      const isClose = key === "ArrowLeft" || event.code === "NumpadSubtract" || key === "-";
      const isSum = key === "ArrowDown";

      if (!isPrevious && !isNext && !isRedraw && !isClose && !isSum) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      if (isSum) return;

      if (isClose && !event.repeat) {
        setShowModal(false);
        setSelectedBumpedOrder(null);
        return;
      }

      if (isPrevious) {
        moveSelection(-1);
        return;
      }

      if (isNext) {
        moveSelection(1);
        return;
      }

      if (isRedraw && !event.repeat) {
        bumpBarUnbumpSelectedRef.current?.();
      }
    };

    window.addEventListener("keydown", handleBumpBarModalKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleBumpBarModalKeyDown, true);
    };
  }, [
    bumpedOrders,
    confirmUnbump.open,
    selectedBumpedOrder,
    selectedBumpedOrderFullyVoided,
    settings?.enable_bump_bar,
    showModal,
  ]);

  const totalTickets = React.useMemo(() => {
    const visible = orders.filter((o) => !o.isParked);
    const ids = new Set(visible.map((o) => o.ticket_instance_id));
    return ids.size;
  }, [orders]);

  const menuButtonClass =
    "bg-slate-600 dark:bg-slate-500 text-white px-4 py-2 rounded hover:bg-slate-700 dark:hover:bg-slate-400 active:scale-[0.98] touch-manipulation select-none";
  const submenuButtonClass =
    "min-w-[110px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-5 py-4 text-base font-semibold text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98] touch-manipulation select-none";

  const renderBubbleMenu = (children) => (
    <div className="absolute bottom-full left-1/2 mb-3 -translate-x-1/2 z-50">
      <div
        className="relative inline-flex origin-bottom items-center gap-3 rounded-2xl border border-gray-200 bg-gray-100/95 px-4 py-4 shadow-2xl shadow-slate-950/30 dark:border-gray-700 dark:bg-gray-900/95"
        style={{
          animation: "footerMenuExpand 180ms ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes footerMenuExpand {
          0% {
            opacity: 0;
            transform: scale(0.92) translateY(8px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>

      <footer className="flex h-14 shrink-0 items-center justify-between border-t bg-white px-6 shadow-sm dark:border-gray-700 dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-100">
        <div className="flex flex-col items-start justify-center font-mono text-xs space-y-1">
          <span className="leading-none flex items-center">{clock}</span>

          {pageIndicatorText ? (
            <span className="leading-none flex items-center text-gray-500 dark:text-gray-400">
              {pageIndicatorText}
            </span>
          ) : totalPages > 1 ? (
            <span className="leading-none flex items-center text-gray-500 dark:text-gray-400">
              Page {currentPage + 1} of {totalPages}
            </span>
          ) : null}

          <span className="leading-none flex text-gray-500 dark:text-gray-400 items-center">
            Tickets: {totalTickets}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {(showExposeButton || showSortButton || showPinButton) && (
            <div ref={freeformToolboxRef} className="relative">
              {showFreeformToolbox &&
                renderBubbleMenu(
                  <>
                    {showExposeButton && (
                      <button
                        onClick={() => {
                          setShowFreeformToolbox(false);
                          onExposeClick?.();
                        }}
                        className={submenuButtonClass}
                      >
                        Expose
                      </button>
                    )}

                    {showSortButton && (
                      <button
                        onClick={() => {
                          setShowFreeformToolbox(false);
                          onSortClick?.();
                        }}
                        className={submenuButtonClass}
                      >
                        Sort
                      </button>
                    )}

                    {showPinButton && (
                      <button
                        onClick={() => {
                          setShowFreeformToolbox(false);
                          onPinClick?.();
                        }}
                        className={submenuButtonClass}
                      >
                        PIN
                      </button>
                    )}
                  </>
                )}

              <button
                onClick={() => {
                  setShowFireMenu(false);
                  setShowPrintMenu(false);
                  setShowFreeformToolbox((prev) => !prev);
                }}
                className={menuButtonClass}
              >
                Freeform Toolbox
              </button>
            </div>
          )}

          <button
            onClick={onParkClick}
            onTouchStart={(e) => {
              e.preventDefault();
              onParkClick?.();
            }}
            className={`${menuButtonClass} inline-flex items-center gap-2`}
          >
            <FooterIcon icon="fluent-emoji:inbox-tray" /> Park
          </button>

          <div ref={fireMenuRef} className="relative">
            {showFireMenu &&
              renderBubbleMenu(
                <>
                  <button
                    onClick={() => {
                      setShowFireMenu(false);
                      onFireTicket?.();
                    }}
                    className={submenuButtonClass}
                  >
                    Ticket
                  </button>

                  {settings?.coursing && (
                    <button
                      onClick={() => {
                        setShowFireMenu(false);
                        onFireCourse?.();
                      }}
                      className={submenuButtonClass}
                    >
                      Course
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowFireMenu(false);
                      onFireItem?.();
                    }}
                    className={submenuButtonClass}
                  >
                    Item
                  </button>
                </>
              )}

            <button
              onClick={() => {
                setShowPrintMenu(false);
                setShowFireMenu((prev) => !prev);
              }}
              className={`${menuButtonClass} inline-flex items-center gap-2`}
            >
              <FooterIcon icon="fluent-emoji:fire" /> Fire
            </button>
          </div>

          {settings?.enable_printing_from_ods && (
            <div ref={printMenuRef} className="relative">
              {showPrintMenu &&
                renderBubbleMenu(
                  <>
                    <button
                      onClick={() => {
                        setShowPrintMenu(false);
                        onPrintTicket?.();
                      }}
                      className={submenuButtonClass}
                    >
                      Ticket
                    </button>

                    <button
                      onClick={() => {
                        setShowPrintMenu(false);
                        onPrintCourse?.();
                      }}
                      className={submenuButtonClass}
                    >
                      Course
                    </button>
                  </>
                )}

              <button
                onClick={() => {
                  setShowFireMenu(false);
                  setShowPrintMenu((prev) => !prev);
                }}
                className={`${menuButtonClass} inline-flex items-center gap-2`}
              >
                <FooterIcon icon="fluent-emoji:printer" /> Print
              </button>
            </div>
          )}

          {(settings?.hide_course || settings?.hide_bumped_courses || settings?.item_prep_display_mode === "hidden") && (
            <button
              onClick={onViewFullTicket}
              onTouchStart={(e) => {
                e.preventDefault();
                onViewFullTicket?.();
              }}
              className={`${menuButtonClass} inline-flex items-center gap-2`}
            >
              <FooterIcon icon="fluent-emoji:magnifying-glass-tilted-left" /> Full Ticket
            </button>
          )}

          <button
            onClick={openUnbumpModal}
            onTouchStart={(e) => {
              e.preventDefault();
              openUnbumpModal();
            }}
            className={`${menuButtonClass} inline-flex items-center gap-2`}
          >
            <FooterIcon icon="fluent-emoji:repeat-button" /> Unbump
          </button>

          <button
            onClick={generateSummary}
            onTouchStart={(e) => {
              e.preventDefault();
              generateSummary();
            }}
            className="inline-flex items-center gap-2 bg-indigo-700 text-white px-4 py-2 rounded hover:bg-indigo-800 active:scale-[0.98] touch-manipulation select-none"
          >
            <FooterIcon icon="fluent-emoji:bar-chart" /> Show Summary
          </button>
        </div>
      </footer>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 text-black dark:text-white p-4 rounded shadow-md w-[95vw] max-w-7xl max-h-[90vh] overflow-hidden">
            <h2 className="text-lg font-bold mb-3">Bumped Tickets (Today)</h2>

            {bumpedOrders.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 text-center">
                No bumped tickets found.
              </div>
            ) : (
              <div className="flex h-[72vh] gap-4">
                <div className="w-[320px] shrink-0 border-r border-gray-200 dark:border-gray-700 pr-3 overflow-y-auto">
                  <div className="space-y-2">
                    {bumpedOrders.map((order) => {
                      const isSelected =
                        selectedBumpedOrder?.ticket_instance_id === order.ticket_instance_id;

                      return (
                        <button
                          key={order.ticket_instance_id}
                          type="button"
                          onClick={() => setSelectedBumpedOrder(order)}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            setSelectedBumpedOrder(order);
                          }}
                          className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                            isSelected
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                              : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60"
                          }`}
                        >
                          <div className="text-sm font-semibold">
                            #{order.order_id} - {order.table_name || "No table"}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {order.server_name || "No server"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="min-w-0 flex-1 overflow-y-auto">
                  {selectedBumpedOrder ? (
                    <div className="flex flex-col items-center">
                      <div className="mb-3 flex w-full max-w-xl items-center justify-between gap-3">
                        <div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Previewing ticket
                          </div>
                          <div className="text-base font-semibold">
                            #{selectedBumpedOrder.order_id} - {selectedBumpedOrder.table_name || "No table"}
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (selectedBumpedOrderFullyVoided) return;
                            if (isLineItemMode) {
                              const ok = await doUnbump({
                                ticket_instance_id: selectedBumpedOrder.ticket_instance_id,
                                order_id: selectedBumpedOrder.order_id ?? null,
                              }, true);
                              if (ok) setShowModal(false);
                              return;
                            }
                            setConfirmUnbump({
                              open: true,
                              ticket_instance_id: selectedBumpedOrder.ticket_instance_id,
                              order_id: selectedBumpedOrder.order_id ?? null,
                            });
                          }}
                          disabled={selectedBumpedOrderFullyVoided}
                          className={`rounded-xl px-5 py-3 text-sm font-semibold text-white active:scale-[0.98] touch-manipulation ${
                            selectedBumpedOrderFullyVoided
                              ? "cursor-not-allowed bg-gray-400 dark:bg-gray-600"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                        >
                          {isLineItemMode ? "Unbump All Items" : "Unbump"}
                        </button>
                      </div>

                      {selectedBumpedOrderFullyVoided && (
                        <div className="mb-3 w-full max-w-xl rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                          This ticket cannot be unbumped because all items are voided.
                        </div>
                      )}

                      <div className="w-full flex justify-center">
                        <div className="max-w-xl">
                          <TicketCard
                            order={selectedBumpedOrder}
                            orderIndex={0}
                            totalCards={1}
                            isPeekMode
                            disableBottomCutoffFade
                            settings={{
                              ...settings,
                              hide_bumped_courses: false,
                              hide_course: false,
                            }}
                            onItemTap={() => {}}
                            onItemDoubleTap={() => {}}
                            onBump={() => {}}
                            onBumpCourse={() => {}}
                            onCourseComplete={() => {}}
                            isPrepScreen={false}
                            isFireCourseMode={false}
                            onSelectFireCourse={() => {}}
                            setIsFireCourseMode={() => {}}
                            isFireItemMode={false}
                            onSelectFireItem={() => {}}
                            isPrintCourseMode={false}
                            onSelectPrintCourse={() => {}}
                            isFireTicketMode={false}
                            isPrintTicketMode={false}
                            setPeekedOrder={() => {}}
                            setPeekModeIntent={() => {}}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="text-right mt-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedBumpedOrder(null);
                  setConfirmUnbump({
                    open: false,
                    ticket_instance_id: null,
                    order_id: null,
                  });
                }}
                className="text-sm px-3 py-1 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400 dark:hover:bg-gray-500 active:scale-[0.98] touch-manipulation"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmUnbump.open && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]"
          onClick={() =>
            setConfirmUnbump({
              open: false,
              ticket_instance_id: null,
              order_id: null,
            })
          }
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 text-black dark:text-white w-[90vw] max-w-lg rounded-2xl shadow-2xl p-6"
          >
            <h3 className="text-2xl font-bold mb-3 text-center">Unbump All Items?</h3>

            <p className="text-base text-gray-700 dark:text-gray-300 mb-6 text-center leading-relaxed">
              This ticket will be restored to the screen.
              <br />
              Do you also want to unbump all items?
            </p>

            <div className="flex flex-col gap-4">
              <button
                onClick={async () => {
                  const id = confirmUnbump.ticket_instance_id;
                  const orderId = confirmUnbump.order_id;
                  setConfirmUnbump({
                    open: false,
                    ticket_instance_id: null,
                    order_id: null,
                  });
                  if (!id) return;

                  const ok = await doUnbump(
                    { ticket_instance_id: id, order_id: orderId },
                    true
                  );
                  if (ok) setShowModal(false);
                }}
                className="w-full h-14 text-lg font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] transition touch-manipulation"
              >
                Unbump All Items
              </button>

              <button
                onClick={async () => {
                  const id = confirmUnbump.ticket_instance_id;
                  const orderId = confirmUnbump.order_id;
                  setConfirmUnbump({
                    open: false,
                    ticket_instance_id: null,
                    order_id: null,
                  });
                  if (!id) return;

                  const ok = await doUnbump(
                    { ticket_instance_id: id, order_id: orderId },
                    false
                  );
                  if (ok) setShowModal(false);
                }}
                className="w-full h-14 text-lg font-semibold rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 active:scale-[0.98] transition touch-manipulation"
              >
                Keep Items Bumped
              </button>
            </div>
          </div>
        </div>
      )}

      {showSummary && (
        <div
          className={`fixed top-14 right-0 h-[calc(100%-3.5rem)] w-80 bg-white/90 dark:bg-gray-900/80 border-l border-gray-300 dark:border-gray-700 shadow-lg transform transition-transform duration-300 z-40 ${
            showSummary ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-4 h-full flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-bold">Item Summary</h2>
              <button
                onClick={() => setShowSummary(false)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  setShowSummary(false);
                }}
                className="text-sm px-2 py-1 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400 dark:hover:bg-gray-500 active:scale-[0.98] touch-manipulation"
              >
                Close
              </button>
            </div>

            <ul className="flex-1 overflow-y-auto space-y-4 text-sm scrollbar-hide">
              {summaryData.length === 0 ? (
                <li className="text-gray-500">No items on screen.</li>
              ) : (
                summaryData.map((section, idx) => (
                  <li key={idx}>
                    <h3 className="font-bold text-indigo-700 dark:text-indigo-300 mb-1">
                      Course {section.course + 1}
                    </h3>
                    <ul className="space-y-1">
                      {section.items.map((item, itemIdx) => (
                        <li
                          key={itemIdx}
                          className="border-b-4 border-purple-500 dark:border-purple-300 pb-2"
                        >
                          <div className="flex justify-between font-semibold">
                            <span>{item.name}</span>
                            <span>x{item.qty}</span>
                          </div>

                          {item.prepGroups && (
                            <ul className="pl-3 mt-1 text-xs text-gray-600 dark:text-gray-300 space-y-2">
                              {item.prepGroups.map((prep, prepIdx) => (
                                <li
                                  key={prepIdx}
                                  className={`pt-2 ${
                                    prepIdx !== 0
                                      ? "border-t border-gray-300 dark:border-gray-300"
                                      : ""
                                  }`}
                                >
                                  <ul className="space-y-0.5">
                                    {prep.map((cond, i) => (
                                      <li key={i} className="flex justify-between">
                                        <span>- {cond.condName}</span>
                                        <span>x{cond.count}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              ))}
                            </ul>
                          )}

                          {!item.prepGroups && item.condiments?.length > 0 && (
                            <ul className="pl-3 mt-1 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                              {item.condiments.map((cond, i) => (
                                <li key={i} className="flex justify-between">
                                  <span>- {cond.condName}</span>
                                  <span>x{cond.count}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

